import React from 'react'

export interface IconBaseProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon. Can be a number (which will be treated as pixels) or a string (e.g., "24px", "1rem", "100%").
   * If not provided, defaults to 24px.
   */
  size?: number | string
  /**
   * Color of the icon. Can be any valid CSS color value.
   * If not provided, defaults to "currentColor" to inherit from text color.
   */
  color?: string
  /**
   * Accessible title for the icon. If provided, the icon will have a title element.
   * If decorative is true, this will be ignored.
   */
  title?: string
  /**
   * Whether the icon is decorative (purely visual and should be hidden from screen readers).
   * If true, aria-hidden="true" will be set and title will be ignored.
   * If false and title is provided, aria-labelledby will be used.
   */
  decorative?: boolean
  /**
   * Additional CSS classes to apply to the SVG element.
   */
  className?: string
  /**
   * The viewBox attribute of the SVG. Defaults to "0 0 24 24".
   */
  viewBox?: string
  /**
   * The children (path elements) to render inside the SVG.
   */
  children: React.ReactNode
}

/**
 * IconBase is a reusable base component for creating inline SVG icons.
 * It provides consistent handling of size, color, accessibility, and styling.
 */
export const IconBase = React.forwardRef<SVGSVGElement, IconBaseProps>(
  (
    {
      size = 16,
      color = 'currentColor',
      title,
      decorative = false,
      className,
      viewBox = '0 0 24 24',
      children,
      style: propStyle,
      ...svgProps
    },
    ref
  ) => {
    // Convert size to string if it's a number
    const sizeString = typeof size === 'number' ? `${size}px` : size

    // Generate unique ID for title element if title is provided
    const titleId =
      title && !decorative
        ? `icon-title-${Math.random().toString(36).substr(2, 9)}`
        : undefined

    // Build style object for color (only set if not default currentColor)
    const style = color !== 'currentColor' ? { color, ...propStyle } : propStyle

    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBox}
        width={sizeString}
        height={sizeString}
        fill="none"
        style={style}
        className={className}
        aria-hidden={decorative || !title ? true : undefined}
        aria-labelledby={title && !decorative ? titleId : undefined}
        role={decorative ? undefined : 'img'}
        {...svgProps}
      >
        {title && !decorative && titleId && <title id={titleId}>{title}</title>}
        {children}
      </svg>
    )
  }
)

IconBase.displayName = 'IconBase'
